'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import * as Sentry from '@sentry/nextjs';
import posthog from 'posthog-js';
import { TIP_TIERS } from '@peach/types/payments/price-limits.js';
import { tw } from '@tw';
import { createDonationCheckout } from '@/app/actions/donations';
import { formatPrice } from '@/lib/utils/format';
import { callAction } from '@/lib/call-action';
import { TIP_PLACEHOLDER, TIP_RANGE_HINT, tipFeeLine, useTipAmount } from './useTipAmount';

const EmbeddedCheckoutPanel = dynamic(() => import('@/components/payment/EmbeddedCheckoutPanel'), {
  ssr: false,
});

interface TipCardProps {
  tutorUsername: string;
  tutorName: string;
  /** Inside a sheet that already says what it is: no card edge and no title. */
  bare?: boolean;
  /** Where the tip was asked for, for the checkout events. */
  surface?: string;
  /** Told once the tip is paid, so a sheet around the card can say Done. */
  onThanked?: () => void;
  /** An amount already tapped elsewhere: the payment opens for it at once. */
  preset?: number;
}

/**
 * Never renders nothing: the rating that used to gate the tip is gone, and a
 * tip does not retire the card either — a donor may give any number of times.
 */
export function TipCard({
  tutorUsername,
  tutorName,
  bare = false,
  surface = 'storefront_tip',
  onThanked,
  preset,
}: TipCardProps) {
  const picker = useTipAmount(preset ?? TIP_TIERS[0]!);
  const amount = picker.amount;
  const [busy, setBusy] = useState(!!preset);
  const [error, setError] = useState<string | null>(null);
  const [thanked, setThanked] = useState(false);
  const [embedded, setEmbedded] = useState<{
    clientSecret: string;
    publishableKey: string;
  } | null>(null);

  const usd = (n: number) => formatPrice({ amount: n, currency: 'USD' });
  const feeLine = amount === null ? null : tipFeeLine(tutorName, amount);
  // Stripe's iframe held focus and is gone: the thank-you takes it, so it is read out.
  const thanksRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (thanked) thanksRef.current?.focus();
  }, [thanked]);

  // The request and what the card does with its answer, apart: an amount tapped in the
  // header opens the payment from an effect, and an effect may only set state in a callback.
  const request = useCallback(
    (tip: number) => {
      posthog.capture('donation_checkout_started', {
        provider_username: tutorUsername,
        creator_username: tutorUsername,
        tutor_username: tutorUsername,
        amount: tip,
        surface,
      });
      return callAction(() => createDonationCheckout(tutorUsername, tip));
    },
    [surface, tutorUsername]
  );

  const settle = useCallback(
    (result: Awaited<ReturnType<typeof request>>, tip: number) => {
      setBusy(false);
      if (!result.success) {
        setError('Could not open the payment — try again');
        return;
      }
      const { url, clientSecret, publishableKey } = result.data;
      if (clientSecret && publishableKey) {
        setEmbedded({ clientSecret, publishableKey });
        return;
      }
      if (url) {
        window.location.assign(url);
        return;
      }
      Sentry.captureMessage('Donation checkout came back empty', {
        level: 'error',
        extra: { tutorUsername, amount: tip },
      });
      setError('Could not open the payment — try again');
    },
    [tutorUsername]
  );

  const start = async (tip: number) => {
    setBusy(true);
    setError(null);
    settle(await request(tip), tip);
  };

  // Once per mount: a re-run (Strict Mode, a re-render) would open a second Stripe session.
  const presetStarted = useRef(false);
  useEffect(() => {
    if (!preset || presetStarted.current) return;
    presetStarted.current = true;
    void request(preset).then((result) => settle(result, preset));
  }, [preset, request, settle]);

  // Other is the track's fourth place; a tier lights its own.
  const picked = picker.choice === 'other' ? TIP_TIERS.length : TIP_TIERS.indexOf(picker.choice);

  return (
    <div
      className={
        bare
          ? undefined
          : 'rounded-[var(--sf-radius)] border border-[var(--sf-line)] bg-[var(--sf-card)] px-3.5 py-3'
      }
    >
      {(!bare || thanked) && (
        <span
          ref={thanksRef}
          role={thanked ? 'status' : undefined}
          tabIndex={thanked ? -1 : undefined}
          className="block sf-title text-title-2 font-extrabold text-[var(--sf-ink)] outline-none"
        >
          {thanked ? `${tutorName} has all of it` : `Tip ${tutorName} after a call`}
        </span>
      )}

      {preset && busy && !embedded && <span className="sf-note block">Opening the payment…</span>}

      {!thanked && !embedded && !(preset && busy) && (
        <>
          {/* One track and one sliding fill rather than buttons that each light up: the
              amounts are a single choice, and Other is one of them. */}
          <div
            role="group"
            aria-label="Tip amount"
            className="relative mt-2.5 flex rounded-full bg-[var(--sf-page)] p-1"
          >
            {picked >= 0 && (
              <span
                aria-hidden
                style={{ transform: `translateX(${picked * 100}%)` }}
                className="absolute inset-y-1 left-1 w-[calc((100%-0.5rem)/4)] rounded-full bg-[var(--sf-ink)] transition-transform duration-200 ease-out motion-reduce:transition-none"
              />
            )}
            {[...TIP_TIERS, 'other' as const].map((option, i) => (
              <button
                key={option}
                type="button"
                onClick={() => picker.pick(option)}
                aria-pressed={picked === i}
                className={tw(
                  'mm-btn relative flex-1 rounded-full py-2 sf-num text-p-3 font-extrabold transition-colors',
                  picked === i ? 'text-[var(--sf-page)]' : 'text-[var(--sf-ink)]'
                )}
              >
                {option === 'other' ? 'Other' : usd(option)}
              </button>
            ))}
          </div>

          {picker.choice === 'other' && (
            <label className="mt-2 flex min-h-[48px] items-center gap-1.5 rounded-[14px] border border-[var(--sf-line)] px-3.5 focus-within:border-[var(--sf-ink)]">
              <span className="sr-only">Your amount in dollars</span>
              <span aria-hidden className="text-[16px] font-extrabold text-[var(--sf-ink)]">
                $
              </span>
              <input
                inputMode="numeric"
                autoComplete="off"
                autoFocus
                value={picker.typed}
                onChange={(e) => picker.type(e.target.value)}
                placeholder={TIP_PLACEHOLDER}
                className="w-full bg-transparent text-[16px] font-extrabold text-[var(--sf-ink)] outline-none placeholder:font-normal placeholder:text-[var(--sf-mut)]"
              />
            </label>
          )}

          <span className="sf-note mt-2 block" aria-live="polite">
            {feeLine ?? TIP_RANGE_HINT}
          </span>

          {error && <span className="sf-note mt-1 block text-error">{error}</span>}

          <button
            type="button"
            disabled={busy || amount === null}
            onClick={() => amount !== null && void start(amount)}
            className="mm-btn sf-cta sf-cta-ink mt-2"
          >
            {busy ? 'Opening…' : amount === null ? 'Send' : `Send ${usd(amount)}`}
          </button>
        </>
      )}

      {(embedded || (preset && busy)) && feeLine && (
        <span className="sf-note block">{feeLine}</span>
      )}

      {embedded && (
        <div className="mt-2.5 overflow-hidden rounded-[10px]">
          <EmbeddedCheckoutPanel
            clientSecret={embedded.clientSecret}
            publishableKey={embedded.publishableKey}
            onComplete={() => {
              setEmbedded(null);
              setThanked(true);
              onThanked?.();
              posthog.capture('donation_checkout_completed', {
                provider_username: tutorUsername,
                creator_username: tutorUsername,
                tutor_username: tutorUsername,
                amount,
              });
            }}
          />
        </div>
      )}
    </div>
  );
}
